import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Typography, Box, Paper, IconButton, Stack } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, BoxStyle, BackArrowStyle } from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { SalaryStatementTable } from "./SalaryStatementTable";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { FilterSection } from "pages/FilterContext/FilterRow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { ArrowBackIos } from "@mui/icons-material";
import {
  salaryStatementReportPart1,
  salaryStatementReportPart2,
} from "pages/PayRoll/PayRollHeaders";

const REPORT_TYPE_OPTIONS = [
  { label: "Employee Wise", value: "employee_wise" },
  { label: "Department Wise - Summarized", value: "summarized" },
  { label: "Department Wise - Detailed", value: "detailed" },
];

const summarizedBaseHeaders = [
  { name: "Department", key: "department", type: "string" },
  { name: "W.D", key: "working_days", type: "number" },
  { name: "Payable Hours", key: "payable_hours", type: "time" },
  { name: "OT Hours", key: "ot_hours", type: "time" },
  { name: "Weekoff Hours", key: "weekoff_hours", type: "time" },
  { name: "Holiday Hours", key: "holiday_hours", type: "time" },
  { name: "Gross", key: "gross_pay", type: "number" },
  { name: "OTHER.DED", key: "other_deductions", type: "number" },
  { name: "TOT. DED", key: "total_deductions", type: "number" },
  { name: "NET", key: "net_pay", type: "number" },
];

const detailedBaseHeaders = [
  { name: "Department", key: "department", type: "string" },
  { name: "Employee Name", key: "user_name", type: "string" },
  { name: "Employee ID", key: "employee_id", type: "string" },
  { name: "DOJ", key: "date_of_joining", type: "string" },
  { name: "Per Day", key: "per_day_salary", type: "number" },
  { name: "Per Hour", key: "per_hour_salary", type: "number" },
  { name: "Working Hours", key: "total_hours", type: "string" },
  { name: "Payable Hours", key: "payable_hours", type: "time" },
  { name: "Paid Hours", key: "paid_hours", type: "number" },
  { name: "OT Hours", key: "ot_hours", type: "time" },
  { name: "Weekoff Hours", key: "weekoff_hours", type: "time" },
  { name: "Holiday Hours", key: "holiday_hours", type: "time" },
  { name: "Paid Days", key: "working_days", type: "number" },
  { name: "Fixed Salary", key: "fixed_salary", type: "number" },
  { name: "Gross", key: "gross_pay", type: "number" },
  { name: "OTHER.DED", key: "other_deductions", type: "number" },
  { name: "TOT. DED", key: "total_deductions", type: "number" },
  { name: "NET", key: "net_pay", type: "number" },
];

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// Backend overloads `paid_days` with a plain day-count ("26") for day-based
// employees and an "H:MM" duration ("26:23") for hour/shift-based employees.
// Number("26:23") is NaN, so route it through this parser instead of toNumber
// to convert HH:MM into decimal hours rather than silently dropping to 0.
const parseHhMmToDecimalHours = (value) => {
  const match = /^(\d{1,4}):(\d{2})$/.exec(`${value ?? ""}`.trim());
  if (!match) return null;
  return Number(match[1]) + Number(match[2]) / 60;
};

const toPaidDays = (value) => {
  const decimalHours = parseHhMmToDecimalHours(value);
  return decimalHours !== null ? decimalHours : toNumber(value);
};

const formatDecimalHoursAsHhMm = (decimalHours) => {
  const totalMinutes = Math.round((decimalHours || 0) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const EXPORT_HH_MM_KEYS = new Set([
  "payable_hours",
  "paid_hours",
  "ot_hours",
  "weekoff_hours",
  "holiday_hours",
]);

// Payable/OT/Weekoff/Holiday Hours are only meaningful for Hours-basis payrun;
// hide these columns outright when nothing in the loaded data is Hours-basis.
const HOURS_BASIS_ONLY_HEADER_KEYS = new Set([
  "payable_hours",
  "ot_hours",
  "weekoff_hours",
  "holiday_hours",
]);

const toHoursDecimal = (value) => {
  const parsedHours = parseHhMmToDecimalHours(value);
  return parsedHours !== null ? parsedHours : toNumber(value);
};

// `payrun_basis` drives Paid/Payable/OT Hours visibility and calculation, so
// every comparison against it must agree on casing/whitespace to avoid the
// columns silently disagreeing with each other.
const normalizePayrunBasis = (value) => `${value ?? ""}`.trim().toLowerCase();
const PAYRUN_BASIS_SHIFT = "shift";
const PAYRUN_BASIS_HOURS = "hours";

const formatHoursForExport = (value) => {
  if (value === null || value === undefined || value === "") {
    return "00:00";
  }

  const parsedHhMm = parseHhMmToDecimalHours(value);
  if (parsedHhMm !== null) {
    return formatDecimalHoursAsHhMm(parsedHhMm);
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return formatDecimalHoursAsHhMm(numericValue);
  }

  return "00:00";
};

const withExportTimeFormatters = (headers = []) =>
  headers.map((header) => {
    if (!EXPORT_HH_MM_KEYS.has(header?.key) || header?.customFormatter) {
      return header;
    }

    return {
      ...header,
      customFormatter: (row) => {
        const value = row?.[header.key];
        if (header?.key === "payable_hours" && (value === null || value === undefined || value === "")) {
          return "";
        }

        return formatHoursForExport(value);
      },
    };
  });

const normalizeLabel = (value = "") =>
  value.toString().trim().toLowerCase().replace(/\s+/g, " ");

const isStatutoryDeduction = (name = "") => {
  const normalizedName = normalizeLabel(name);
  return (
    normalizedName.includes("pf") ||
    normalizedName.includes("esi") ||
    normalizedName.includes("uan")
  );
};

const buildDeductionKey = (name, index) =>
  `deduction_${
    name
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "item"
  }_${index}`;

// Calculation components are matched by name across every employee's
// calculation_detail (they're not guaranteed to come back in the same order
// or count for each row), so their key carries no positional index.
const buildCalculationKey = (name) =>
  `calculation_${
    name
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "item"
  }`;

const getCalculationName = (calculation, index) =>
  calculation?.calculation_name ||
  calculation?.name ||
  calculation?.component_name ||
  `Calculation ${index + 1}`;

const buildEarningKey = (name, index) =>
  `earning_${
    name
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "item"
  }_${index}`;

const sumCandidateFields = (record, keys = []) =>
  keys.reduce((total, key) => total + toNumber(record?.[key]), 0);

const getPaymentBreakdown = (salary) => {
  const bank1 =
    sumCandidateFields(salary, [
      "bank_1",
      "bank1",
      "bank_1_amount",
      "bank1_amount",
      "primary_bank_amount",
      "payment_bank_1",
      "payment_bank_amount",
      "bank_amount",
      "source_bank_detail_amount",
    ]) || 0;

  const bank2 = sumCandidateFields(salary, [
    "bank_2",
    "bank2",
    "bank_2_amount",
    "bank2_amount",
    "secondary_bank_amount",
    "payment_bank_2",
  ]);

  const cash = sumCandidateFields(salary, [
    "cash",
    "cash_amount",
    "cash_payment",
  ]);

  if (bank1 || bank2 || cash) {
    return { bank1, bank2, cash };
  }

  const modeOfPayment = normalizeLabel(salary?.mode_of_payment);
  const netPay = toNumber(salary?.net_pay);

  if (modeOfPayment.includes("cash")) {
    return { bank1: 0, bank2: 0, cash: netPay };
  }

  return { bank1: netPay, bank2: 0, cash: 0 };
};

const buildDepartmentTotals = (rows = [], addGrandTotal = false) => {
  const groupedRows = rows.reduce((accumulator, row) => {
    const departmentName = row?.department || "Unassigned";

    if (!accumulator[departmentName]) {
      accumulator[departmentName] = [];
    }

    accumulator[departmentName].push(row);
    return accumulator;
  }, {});

  const departmentNames = Object.keys(groupedRows).sort((first, second) =>
    first.localeCompare(second),
  );

  const summaryRows = departmentNames.map((departmentName) => {
    const departmentRows = groupedRows[departmentName];

    const summary = departmentRows.reduce(
      (accumulator, row) => {
        const nextSummary = {
          ...accumulator,
          department: departmentName,
          working_days: accumulator.working_days + toNumber(row?.working_days),
          paid_hours: accumulator.paid_hours + toNumber(row?.paid_hours),
          payable_hours:
            accumulator.payable_hours + toHoursDecimal(row?.payable_hours),
          ot_hours: accumulator.ot_hours + toHoursDecimal(row?.ot_hours),
          weekoff_hours:
            accumulator.weekoff_hours + toHoursDecimal(row?.weekoff_hours),
          holiday_hours:
            accumulator.holiday_hours + toHoursDecimal(row?.holiday_hours),
          total_hours:
            accumulator.total_hours +
            (parseHhMmToDecimalHours(row?.total_hours) ?? 0),
          gross_pay: accumulator.gross_pay + toNumber(row?.gross_pay),
          total_deductions:
            accumulator.total_deductions + toNumber(row?.total_deductions),
          other_deductions:
            accumulator.other_deductions + toNumber(row?.other_deductions),
          net_pay: accumulator.net_pay + toNumber(row?.net_pay),
          bank_1: accumulator.bank_1 + toNumber(row?.bank_1),
          bank_2: accumulator.bank_2 + toNumber(row?.bank_2),
          cash: accumulator.cash + toNumber(row?.cash),
        };

        Object.keys(row || {}).forEach((key) => {
          if (key.startsWith("earning_")) {
            nextSummary[key] =
              toNumber(nextSummary[key]) + toNumber(row?.[key]);
          }
          if (key.startsWith("deduction_") || key.startsWith("calculation_")) {
            nextSummary[key] =
              toNumber(nextSummary[key]) + toNumber(row?.[key]);
          }
        });

        return nextSummary;
      },
      {
        department: departmentName,
        working_days: 0,
        paid_hours: 0,
        payable_hours: 0,
        ot_hours: 0,
        weekoff_hours: 0,
        holiday_hours: 0,
        total_hours: 0,
        gross_pay: 0,
        other_deductions: 0,
        total_deductions: 0,
        net_pay: 0,
        bank_1: 0,
        bank_2: 0,
        cash: 0,
      },
    );

    return {
      ...summary,
      total_hours: formatDecimalHoursAsHhMm(summary.total_hours),
    };
  });

  if (!addGrandTotal) {
    return summaryRows;
  }

  const grandTotalSummary = summaryRows.reduce(
    (summary, row) => {
      const nextSummary = {
        ...summary,
        working_days: summary.working_days + toNumber(row?.working_days),
        paid_hours: summary.paid_hours + toNumber(row?.paid_hours),
        payable_hours: summary.payable_hours + toHoursDecimal(row?.payable_hours),
        ot_hours: summary.ot_hours + toHoursDecimal(row?.ot_hours),
        weekoff_hours: summary.weekoff_hours + toHoursDecimal(row?.weekoff_hours),
        holiday_hours: summary.holiday_hours + toHoursDecimal(row?.holiday_hours),
        total_hours:
          summary.total_hours +
          (parseHhMmToDecimalHours(row?.total_hours) ?? 0),
        gross_pay: summary.gross_pay + toNumber(row?.gross_pay),
        total_deductions:
          summary.total_deductions + toNumber(row?.total_deductions),
        other_deductions:
          summary.other_deductions + toNumber(row?.other_deductions),
        net_pay: summary.net_pay + toNumber(row?.net_pay),
        bank_1: summary.bank_1 + toNumber(row?.bank_1),
        bank_2: summary.bank_2 + toNumber(row?.bank_2),
        cash: summary.cash + toNumber(row?.cash),
      };

      Object.keys(row || {}).forEach((key) => {
        if (key.startsWith("earning_")) {
          nextSummary[key] = toNumber(nextSummary[key]) + toNumber(row?.[key]);
        }
        if (key.startsWith("deduction_") || key.startsWith("calculation_")) {
          nextSummary[key] = toNumber(nextSummary[key]) + toNumber(row?.[key]);
        }
      });

      return nextSummary;
    },
    {
      department: "Grand Total",
      working_days: 0,
      paid_hours: 0,
      payable_hours: 0,
      ot_hours: 0,
      weekoff_hours: 0,
      holiday_hours: 0,
      total_hours: 0,
      gross_pay: 0,
      other_deductions: 0,
      total_deductions: 0,
      net_pay: 0,
      bank_1: 0,
      bank_2: 0,
      cash: 0,
      rowType: "grand_total",
    },
  );

  const grandTotal = {
    ...grandTotalSummary,
    total_hours: formatDecimalHoursAsHhMm(grandTotalSummary.total_hours),
  };

  return [...summaryRows, grandTotal];
};

// Employee Wise has no department grouping to hang a subtotal off of (unlike
// Summarized/Detailed, which already get a total row via buildDepartmentTotals),
// so the bottom "Total" row is built directly from the currently filtered
// employee rows using the same header metadata that drives the table/export —
// summing every "number" column (Monthly CTC, Gross, Total Deductions, Net
// Pay, dynamic earnings/deductions, etc.) plus the HH:MM hour columns that are
// numeric under the hood but display formatted. Anything else is left blank,
// matching how department/grand-total rows already render in the exporter.
const buildEmployeeWiseGrandTotal = (rows = [], headers = []) => {
  if (rows.length === 0) return null;

  const totals = {
    id: "employee-wise-grand-total",
    rowType: "grand_total",
    user_name: "Total",
  };

  headers.forEach((header) => {
    if (header.type === "number") {
      totals[header.key] = rows.reduce(
        (sum, row) => sum + toNumber(row?.[header.key]),
        0,
      );
    } else if (EXPORT_HH_MM_KEYS.has(header.key)) {
      totals[header.key] = rows.reduce(
        (sum, row) => sum + toHoursDecimal(row?.[header.key]),
        0,
      );
    } else if (header.type === "time") {
      // Covers HH:MM columns not already handled above — Working Hours
      // (total_hours) and Shift Duration (shift_duration) — so the total row
      // sums these too instead of leaving them blank.
      const totalDecimalHours = rows.reduce(
        (sum, row) =>
          sum + (parseHhMmToDecimalHours(row?.[header.key]) ?? 0),
        0,
      );
      totals[header.key] = formatDecimalHoursAsHhMm(totalDecimalHours);
    }
  });

  return totals;
};

const buildDetailedRows = (rows = []) => {
  const groupedRows = rows.reduce((accumulator, row) => {
    const departmentName = row?.department || "Unassigned";

    if (!accumulator[departmentName]) {
      accumulator[departmentName] = [];
    }

    accumulator[departmentName].push(row);
    return accumulator;
  }, {});

  const departmentNames = Object.keys(groupedRows).sort((first, second) =>
    first.localeCompare(second),
  );

  let serialNumber = 1;

  return departmentNames.flatMap((departmentName) => {
    const employees = [...groupedRows[departmentName]].sort((first, second) =>
      (first?.user_name || "").localeCompare(second?.user_name || ""),
    );

    const employeeRows = employees.map((employee) => ({
      ...employee,
      rowType: "employee",
      serial_number: serialNumber++,
    }));

    const [departmentTotal] = buildDepartmentTotals(employees, false);

    return [
      {
        id: `header-${departmentName}`,
        rowType: "department_header",
        department: departmentName,
      },
      ...employeeRows,
      {
        id: `total-${departmentName}`,
        rowType: "department_total",
        department: departmentName,
        ...departmentTotal,
      },
    ];
  });
};

export const SalaryStatementReport = ({ setShowReport }) => {
  const {
    domain,
    token,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [companyId, setCompanyId] = useState(0);
  const [paySheduleDetails, setPayScheduleDetails] = useState(true);
  const [getPayScheduleDetails, setGetPayScheduleDetails] = useState([]);
  const [payScheduleDetailsApi, setPayScheduleDetailsApi] = useState(false);
  const [payScheduleName, setPayScheduleName] = useState("");
  const [listViewPage, setListViewPage] = useState(0);
  const [salaryStatementData, setSalaryStatementData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [reportType, setReportType] = useState(REPORT_TYPE_OPTIONS[0]);
  const {
    result: payShedule,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    companyId !== 0 && companyId
      ? `${domain}payschedule-report/?company=${companyId}&company_id=${topbarCompanyId}`
      : null,
  );
  const [salaryDetails, setSalaryDetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const payrollReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  const subModuleId = payrollReports?.submodules?.find(
    (sub) => sub.sub_module_name === "Salary Statement Report",
  )?.sub_module_id;

  // Read-only report -- filter/sort/download/save-view are view-level actions,
  // not edit actions. Gate on viewer permission (see EPFReport/index.js for
  // the fuller reasoning); editor is always false for Reports submodules in
  // the live payload and was blocking these actions for every user.
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Salary Statement Report",
  );

  useEffect(() => {
    if (topbarCompanyId) {
      setCompanyId(topbarCompanyId);
      reCallApi(true);
      setTimeout(function () {
        setPayScheduleDetails(true);
      }, 100);
    }
  }, [reCallApi, topbarCompanyId]);

  const handlePaySchedule = (event, data) => {
    if (data) {
      setGetPayScheduleDetails(data);
      setPayScheduleName(data.name);
      setPayScheduleDetailsApi(true);
    }
  };

  const handleReportTypeChange = (event, data) => {
    if (data) {
      setReportType(data);
      setListViewPage(0);
    }
  };

  useEffect(() => {
    if (payShedule.length !== 0 && paySheduleDetails && !loading) {
      setGetPayScheduleDetails(payShedule[0] ? payShedule[0] : []);
      setPayScheduleName(payShedule[0] ? payShedule[0]?.name : "");
      setPayScheduleDetails(false);
      if (payShedule[0]) {
        setPayScheduleDetailsApi(true);
      }
    } else if (payShedule.length === 0) {
      setGetPayScheduleDetails([]);
      setPayScheduleName("");
      setSalaryDetails([]);
    }
  }, [loading, payShedule, paySheduleDetails]);

  useEffect(() => {
    const getSalaryStatementReport = async () => {
      setLoader(true);
      const res = await customGetAPI(
        `${domain}payrun/report/?pay_schedule_id=${getPayScheduleDetails?.id}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setSalaryDetails(res?.responseDetails?.payroll_data || []);
        setLoader(false);
      } else {
        setLoader(false);
      }
    };
    if (payScheduleDetailsApi) {
      getSalaryStatementReport();
      setPayScheduleDetailsApi(false);
    }
  }, [domain, getPayScheduleDetails, payScheduleDetailsApi, token, topbarCompanyId]);

  const dynamicEarningHeaders = useMemo(
    () =>
      (salaryDetails?.[0]?.earnings_detail || []).map((earning, index) => {
        const earningName =
          earning?.earning_name ||
          earning?.name ||
          earning?.component_name ||
          `Earning ${index + 1}`;

        return {
          name: earningName,
          key: buildEarningKey(earningName, index),
          type: "number",
          earningIndex: index,
        };
      }),
    [salaryDetails],
  );

  const dynamicDeductionHeaders = useMemo(
    () =>
      (salaryDetails?.[0]?.deductions_detail || []).map((deduction, index) => {
        const deductionName =
          deduction?.deduction_name ||
          deduction?.name ||
          deduction?.component_name ||
          `Deduction ${index + 1}`;

        return {
          name: deductionName,
          key: buildDeductionKey(deductionName, index),
          type: "number",
          deductionIndex: index,
        };
      }),
    [salaryDetails],
  );

  // Calculation components (`calculation_detail`) are computed and stored
  // separately from earnings/deductions — they're shown here for reference
  // only and stay out of the payslip and every earnings/deduction total.
  const dynamicCalculationHeaders = useMemo(() => {
    const seen = new Set();
    const headers = [];
    (salaryDetails || []).forEach((salary) => {
      (salary?.calculation_detail || []).forEach((calculation, index) => {
        const name = getCalculationName(calculation, index);
        const key = buildCalculationKey(name);
        if (!seen.has(key)) {
          seen.add(key);
          headers.push({ name, key, type: "number" });
        }
      });
    });
    return headers;
  }, [salaryDetails]);

  // "Paid Hours" (paid_days_adjustment) doesn't apply to Shift-based payrun —
  // drop the column entirely when every loaded employee is Shift-based.
  const showPaidHours = useMemo(
    () =>
      (salaryDetails || []).length === 0 ||
      (salaryDetails || []).some(
        (salary) =>
          normalizePayrunBasis(salary?.payrun_basis) !== PAYRUN_BASIS_SHIFT,
      ),
    [salaryDetails],
  );

  const showPayableHours = useMemo(
    () =>
      (salaryDetails || []).length === 0 ||
      (salaryDetails || []).some(
        (salary) =>
          normalizePayrunBasis(salary?.payrun_basis) === PAYRUN_BASIS_HOURS,
      ),
    [salaryDetails],
  );

  const employeeWiseHeaders = useMemo(
    () => {
      const headers = [
        ...salaryStatementReportPart1,
        ...dynamicEarningHeaders,
        ...dynamicDeductionHeaders,
        ...dynamicCalculationHeaders,
        ...salaryStatementReportPart2,
      ].filter(
        (header) =>
          (showPaidHours || header.key !== "paid_hours") &&
          (showPayableHours || !HOURS_BASIS_ONLY_HEADER_KEYS.has(header.key)),
      );

      return withExportTimeFormatters(headers);
    },
    [
      dynamicCalculationHeaders,
      dynamicDeductionHeaders,
      dynamicEarningHeaders,
      showPaidHours,
      showPayableHours,
    ],
  );

  const employeeWiseRows = useMemo(
    () =>
      (salaryDetails || []).map((salary, index) => {
        const normalizedBasis = normalizePayrunBasis(salary?.payrun_basis);
        const isHoursBasis = normalizedBasis === PAYRUN_BASIS_HOURS;
        const isShiftBasis = normalizedBasis === PAYRUN_BASIS_SHIFT;
        const paidHoursValue = isShiftBasis
          ? 0
          : toHoursDecimal(salary?.paid_days_adjustment);
        const otHoursValue = toHoursDecimal(salary?.ot_hours);
        const weekoffHoursValue = toHoursDecimal(salary?.weekoff_hours);
        const holidayHoursValue = toHoursDecimal(salary?.holiday_hours);

        const { bank1, bank2, cash } = getPaymentBreakdown(salary);
        const dynamicEarningValues = dynamicEarningHeaders.reduce(
          (accumulator, header) => ({
            ...accumulator,
            [header.key]: toNumber(
              salary?.earnings_detail?.[header.earningIndex]?.amount,
            ),
          }),
          {},
        );
        const dynamicDeductionValues = dynamicDeductionHeaders.reduce(
          (accumulator, header) => ({
            ...accumulator,
            [header.key]: toNumber(
              salary?.deductions_detail?.[header.deductionIndex]?.amount,
            ),
          }),
          {},
        );
        const dynamicCalculationValues = (
          salary?.calculation_detail || []
        ).reduce((accumulator, calculation, index) => {
          const key = buildCalculationKey(getCalculationName(calculation, index));
          return {
            ...accumulator,
            [key]: toNumber(accumulator[key]) + toNumber(calculation?.amount),
          };
        }, {});
        const otherDeductions = (salary?.deductions_detail || []).reduce(
          (total, deduction) =>
            isStatutoryDeduction(
              deduction?.deduction_name ||
                deduction?.name ||
                deduction?.component_name,
            )
              ? total
              : total + toNumber(deduction?.amount),
          0,
        );
        // Backend's `total_deductions` field can go stale when a deduction
        // component (e.g. a newly added one) isn't reflected in it yet, even
        // though `net_pay` is already computed off the correct, larger total
        // (net_pay = gross_pay - actual deductions). When an itemized
        // breakdown is present, sum it directly so the displayed total stays
        // in sync with the individual deduction columns and with net_pay;
        // otherwise fall back to the backend's own total.
        const totalDeductions =
          salary?.deductions_detail?.length > 0
            ? salary.deductions_detail.reduce(
                (total, deduction) => total + toNumber(deduction?.amount),
                0,
              )
            : toNumber(salary?.total_deductions);

        return {
          ...salary,
          id: salary?.id || salary?.employee_id || `employee-${index}`,
          department:
            salary?.department || salary?.department_name || "Unassigned",
          total_present_days: toNumber(salary?.total_present_days),
          total_leave_days: toNumber(salary?.total_leave_days),
          working_days: toPaidDays(salary?.paid_days),
          payable_hours: isHoursBasis
            ? paidHoursValue +
              otHoursValue +
              weekoffHoursValue +
              holidayHoursValue
            : "",
          ot_hours: isHoursBasis ? otHoursValue : "",
          weekoff_hours: isHoursBasis ? weekoffHoursValue : "",
          holiday_hours: isHoursBasis ? holidayHoursValue : "",
          paid_hours: isShiftBasis ? "" : paidHoursValue,
          fixed_salary: toNumber(salary?.fixed_salary || salary?.monthly_ctc),
          gross_pay: toNumber(salary?.gross_pay),
          other_deductions: otherDeductions,
          total_deductions: totalDeductions,
          net_pay: toNumber(salary?.net_pay),
          total_hours: salary?.total_hours || "",
          bank_1: bank1,
          bank_2: bank2,
          cash,
          ...dynamicEarningValues,
          ...dynamicDeductionValues,
          ...dynamicCalculationValues,
        };
      }),
    [dynamicDeductionHeaders, dynamicEarningHeaders, salaryDetails],
  );

  const summarizedRows = useMemo(
    () => buildDepartmentTotals(employeeWiseRows, true),
    [employeeWiseRows],
  );

  const summarizedHeaders = useMemo(() => {
    const headers = [
      ...summarizedBaseHeaders.slice(0, 3),
      ...dynamicEarningHeaders,
      ...dynamicDeductionHeaders,
      ...dynamicCalculationHeaders,
      ...summarizedBaseHeaders.slice(3),
    ].filter(
      (header) =>
        showPayableHours || !HOURS_BASIS_ONLY_HEADER_KEYS.has(header.key),
    );

    return withExportTimeFormatters(headers);
  }, [
    dynamicCalculationHeaders,
    dynamicDeductionHeaders,
    dynamicEarningHeaders,
    showPayableHours,
  ]);

  const detailedHeaders = useMemo(
    () => {
      const fixedSalaryIndex = detailedBaseHeaders.findIndex(
        (header) => header.key === "fixed_salary",
      );
      const headers = [
        ...detailedBaseHeaders.slice(0, fixedSalaryIndex),
        ...dynamicEarningHeaders,
        ...dynamicDeductionHeaders,
        ...dynamicCalculationHeaders,
        ...detailedBaseHeaders.slice(fixedSalaryIndex),
      ].filter(
        (header) =>
          (showPaidHours || header.key !== "paid_hours") &&
          (showPayableHours || !HOURS_BASIS_ONLY_HEADER_KEYS.has(header.key)),
      );

      return withExportTimeFormatters(headers);
    },
    [
      dynamicCalculationHeaders,
      dynamicDeductionHeaders,
      dynamicEarningHeaders,
      showPaidHours,
      showPayableHours,
    ],
  );

  const supportsSavedViews = REPORT_TYPE_OPTIONS.some(
    (option) => option.value === reportType?.value,
  );

  const activeFilterHeaders = useMemo(() => {
    switch (reportType?.value) {
      case "summarized":
        return summarizedHeaders;
      case "detailed":
        return detailedHeaders;
      default:
        return employeeWiseHeaders;
    }
  }, [detailedHeaders, employeeWiseHeaders, reportType, summarizedHeaders]);

  const handleRestoreReportType = useCallback((scopeValue) => {
    const restoredReportType = REPORT_TYPE_OPTIONS.find(
      (option) => option.value === scopeValue,
    );
    if (restoredReportType) {
      setReportType(restoredReportType);
    }
  }, []);

  const handleSavedViewChange = useCallback(() => {
    setListViewPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "salary_statement_saved_views",
    scope: reportType?.value,
    fullHeaders: activeFilterHeaders,
    screenAccess: check_access,
    enabled: supportsSavedViews,
    onRestoreScope: handleRestoreReportType,
    onViewChange: handleSavedViewChange,
  });

  const activeFilterData = useMemo(() => {
    switch (reportType?.value) {
      case "summarized":
        return summarizedRows;
      case "detailed":
        return employeeWiseRows;
      default:
        return employeeWiseRows;
    }
  }, [employeeWiseRows, reportType, summarizedRows]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || userDetails?.company_name || "N/A",
      "Pay Schedule Name": payScheduleName || "N/A",
      "Report Type": reportType?.label || "N/A",
      "Report Generated On": new Date().toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    }),
    [payScheduleName, reportType, topbarCompanyName, userDetails?.company_name],
  );

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      if (reportType?.value === "summarized") {
        setSalaryStatementData(filteredData);
      } else if (reportType?.value === "detailed") {
        setSalaryStatementData(buildDetailedRows(filteredData));
      } else {
        const grandTotalRow = buildEmployeeWiseGrandTotal(
          filteredData,
          employeeWiseHeaders,
        );
        setSalaryStatementData(
          grandTotalRow ? [...filteredData, grandTotalRow] : filteredData,
        );
      }

      if (listViewPage * rowsPerPage >= filteredData.length) {
        setListViewPage(0);
      }
    },
    [employeeWiseHeaders, listViewPage, reportType, rowsPerPage],
  );

  return (
    <ThemeProvider theme={theme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box
          sx={{
            ...BoxStyle,
            height: "calc(100vh - 72px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <IconButton
              onClick={() => setShowReport(false)}
              sx={BackArrowStyle}
            >
              <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
            </IconButton>

            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}
              >
                Salary Statement
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Salary Statement Report provides a comprehensive overview of the
                salary details for employees based on the selected pay schedule,
                including earnings, deductions, and net pay.
              </Typography>
            </Box>
          </Stack>

          <FilterSection
            filterLabel="Filters"
            filters={[
              {
                width: 480,
                props: {
                  dataList: payShedule.map((obj) => ({
                    ...obj,
                    label: obj.name,
                  })),
                  selectedValue: payScheduleName,
                  updateDataFunction: handlePaySchedule,
                  Icons: PersonOutlineIcon,
                  staticLabel: "PaySchedule",
                },
              },
              {
                width: 460,
                props: {
                  dataList: REPORT_TYPE_OPTIONS,
                  selectedValue: reportType,
                  updateDataFunction: handleReportTypeChange,
                  Icons: PersonOutlineIcon,
                  staticLabel: "Report Type",
                  sx: {
                    "& .MuiAutocomplete-inputRoot": {
                      paddingLeft: "124px !important",
                      paddingRight: "56px !important",
                    },
                    "& .MuiInputBase-input": {
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    },
                  },
                },
              },
            ]}
          />

          <Paper
            sx={{
              width: "100%",
              mt: 2,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CustomFilter
              title="Salary Statement"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={activeFilterData}
              filteredData={salaryStatementData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              showtitle_count={true}
              hideCount={true}
              showFilter={true}
              filters={titleOverride}
              showSavedViewControl={false}
              newButtonComponent={newButtonComponent}
              includeMetadata={true}
              metadata={reportMetadata}
              downloadFormats={["excel", "pdf", "csv"]}
            />

            <Mui.Grid
              sx={{
                ml: 2,
                mr: 2,
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {loader ? (
                <Loader />
              ) : (
                <SalaryStatementTable
                  dataLength={activeFilterData}
                  salaryDetails={salaryStatementData}
                  listViewPage={listViewPage}
                  setListViewPage={setListViewPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  reportType={reportType?.value}
                  employeeWiseHeaders={visibleHeaders}
                  summaryHeaders={visibleHeaders}
                  detailedHeaders={visibleHeaders}
                />
              )}
            </Mui.Grid>
          </Paper>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
